
import { createSelector } from "@reduxjs/toolkit";
import { useEffect } from "react";
import store, { AppDispatch, AppState, UsePortalSelector } from "../..";
import { loadStatus as loadStatusEnum } from "../entitiesType";
import { RefreshQuotePricing } from "../quotes";
import { getFullListOfParts, loadHwItemsByQuoteId } from ".";
import { HardwareRequestItemFromOptions, QuoteHardwareRequestItemStore } from "../../../powershadesApiTypes";

/**
 * Helper function to get the correct quoteId from different sources
 * @param providedQuoteId - The initially provided quoteId
 * @returns The correct quoteId or 0 if none found
 */
const getCorrectQuoteId = (providedQuoteId: number): number => {
	if (providedQuoteId > 0) {
		return providedQuoteId;
	}

	try {
		const urlParams = new URLSearchParams(window.location.search);
		const urlQuoteId = urlParams.get('quoteID');
		if (urlQuoteId) {
			const parsedId = parseInt(urlQuoteId, 10);
			if (parsedId > 0) {
				return parsedId;
			}
		}

		const { hash } = window.location;
		const hashMatch = hash.match(/quoteID=(\d+)/);
		if (hashMatch && hashMatch[1]) {
			const parsedId = parseInt(hashMatch[1], 10);
			if (parsedId > 0) {
				return parsedId;
			}
		}

		const state = store.getState();
		if (state.entity.quotes) {
			const quotes = (state.entity.quotes as any).quotes || {};
			const loadedQuoteIds = Object.keys(quotes)
				.map((id) => parseInt(id, 10))
				.filter((id) => id > 0 && quotes[id]?.loadStatus === loadStatusEnum.fullyLoaded);
			
			if (loadedQuoteIds.length === 1 && loadedQuoteIds[0]) {
				return loadedQuoteIds[0];
			}
		}

		return 0;
	} catch (error) {
		console.error('Could not find quoteId for assembly parts:', error);
		return 0;
	}
};



const selectHWRItemsByQuoteId = (QuoteId: number) => (state: AppState) => {
    const hwItems = state.entity.hardwareRequestItems[QuoteId] ?? {
        loadStatus: loadStatusEnum.needsLoaded,
        Items: {}
    };
    const {loadStatus, ...hwItemsTwo} = hwItems ?? {};

    if( loadStatus !== loadStatusEnum.loading && loadStatus !== loadStatusEnum.fullyLoaded) {
        // I don't know why the type system is not catching this as a AppDispatch
        const dispatch = store.dispatch as AppDispatch;
        dispatch(loadHwItemsByQuoteId(QuoteId)).then(() => {
            dispatch(RefreshQuotePricing({quoteId: QuoteId}));
        });
        return {} as Record<number,QuoteHardwareRequestItemStore>;
    }

    return hwItemsTwo.Items as Record<number,QuoteHardwareRequestItemStore>;
}

const selectHWRItemsListByQuoteId = (quoteId: number) =>createSelector(selectHWRItemsByQuoteId(quoteId), (hwItems) => {
    return Object.values(hwItems).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
});

const useHWRItemsByQuoteId = (QuoteId: number) => UsePortalSelector(selectHWRItemsListByQuoteId(QuoteId));

const selectFullHWRItemsByQuoteId = (QuoteId: number) => (state: AppState) => {
	const correctQuoteId = getCorrectQuoteId(QuoteId);
	
	if (correctQuoteId <= 0) {
		return {} as Record<number, HardwareRequestItemFromOptions>;
	}

	const hwItems = state.entity.hardwareRequestItems[correctQuoteId] ?? {
		loadStatus: loadStatusEnum.needsLoaded,
		fullLoadStatus: loadStatusEnum.needsLoaded,
		Items: {},
		TotalItems: {}
	};
	const { fullLoadStatus, ...hwItemsTwo } = hwItems ?? {};

	const fullKeyCount = Object.keys(hwItemsTwo.TotalItems ?? {}).length;

	const isLoadingCondition = fullLoadStatus !== loadStatusEnum.loading && fullLoadStatus !== loadStatusEnum.fullyLoaded;
	const isEmptyCondition = fullKeyCount === 0 && fullLoadStatus !== loadStatusEnum.loading;
	
	if (correctQuoteId > 0 && (isLoadingCondition || isEmptyCondition)) {
		return {} as Record<number, HardwareRequestItemFromOptions>;
	}

	return hwItemsTwo.TotalItems as Record<number, HardwareRequestItemFromOptions>;
};

const selectFullHWRItemsListByQuoteId = (quoteId: number) =>createSelector(selectFullHWRItemsByQuoteId(quoteId), (hwItems) => {
    return Object.values(hwItems).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}
);

const useFullHWRItemsByQuoteId = (QuoteId: number) => {
	const correctQuoteId = getCorrectQuoteId(QuoteId);
	const data = UsePortalSelector(selectFullHWRItemsListByQuoteId(correctQuoteId));
	
	useEffect(() => {
		if (correctQuoteId <= 0) {
			return;
		}

		const state = store.getState();
		const hwItems = state.entity.hardwareRequestItems[correctQuoteId];
		
		if (!hwItems) {
			const dispatch = store.dispatch as AppDispatch;
			dispatch(getFullListOfParts({ quoteId: correctQuoteId }));
			return;
		}

		const { fullLoadStatus } = hwItems;
		const fullKeyCount = Object.keys(hwItems.TotalItems ?? {}).length;
		
		const needsLoading = fullLoadStatus !== loadStatusEnum.loading
			&& fullLoadStatus !== loadStatusEnum.fullyLoaded;
		const isEmpty = fullKeyCount === 0 && fullLoadStatus !== loadStatusEnum.loading;
		
		if (needsLoading || isEmpty) {
			const dispatch = store.dispatch as AppDispatch;
			dispatch(getFullListOfParts({ quoteId: correctQuoteId }));
		}
	}, [correctQuoteId]);
	
	return data;
};


export {
    useHWRItemsByQuoteId,
    selectHWRItemsByQuoteId,
    selectHWRItemsListByQuoteId,
    selectFullHWRItemsByQuoteId,
    useFullHWRItemsByQuoteId
};